/**
 * /doctor-recommendation-vietnam
 *
 * Single SEO landing page — Doctor Recommendation in Vietnam.
 * Built data-driven with the existing site components to inherit the
 * established branding, palette and mobile layout. No new design work.
 *
 * Safety wording (per copy spec):
 *   • Independent platform — not a hospital or clinic.
 *   • No medical diagnosis, no result guarantees.
 *   • Never claims "the best doctor" as a fact.
 *   • Coordinator language only.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight, ShieldCheck, MessageCircle, Stethoscope, ClipboardCheck,
  AlertTriangle, CheckCircle2, Languages, Heart,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  UrgentIntro, UrgentCardGrid, UrgentSteps, UrgentFaq, UrgentEditorial,
  UrgentCtaBand,
} from '../components/UrgentCareSections';
import { useContactInfo, getWhatsAppHref } from '../contexts/ContactInfoContext';
import { useCatalog } from '../context/CatalogContext';
import { getInfoLandingImage } from '../lib/imageOverrides';

const SITE = 'https://vietclinics.com';
const SLUG = '/doctor-recommendation-vietnam';

const HERO_IMAGE =
  'https://images.pexels.com/photos/4173251/pexels-photo-4173251.jpeg?auto=compress&cs=tinysrgb&w=1400';
const HERO_FALLBACK =
  'https://images.pexels.com/photos/7088530/pexels-photo-7088530.jpeg?auto=compress&cs=tinysrgb&w=1400';

const HOW_WE_HELP = [
  { title: 'Understanding your treatment needs',          body: 'We start with a short enquiry — your condition, treatment of interest, location preferences and timing — so recommendations match your real situation, not a generic list.' },
  { title: 'Reviewing your city & travel plans',          body: 'We factor in where you are or where you plan to travel, so suggested doctors and clinics are practical to reach for both consultation and follow-up.' },
  { title: 'Matching with suitable doctors',              body: 'We share licensed, experienced doctor and clinic options from our verified partner network, with credentials and English-language notes up front.' },
  { title: 'Arranging consultations',                     body: 'We help coordinate online or in-person consultations so you can ask questions and meet the doctor before any travel or commitment.' },
  { title: 'Helping you prepare questions',               body: 'We share a tailored set of questions to ask during the consultation, so you can compare doctors clearly and confidently.' },
  { title: 'Helping you compare options',                 body: 'Different doctors may suggest different approaches. We help you understand the differences in technique, recovery and price without pressure.' },
  { title: 'Supporting communication',                    body: 'Where the lead doctor does not work fully in English, we coordinate translator support so consultations stay clear.' },
  { title: 'Coordinating next steps',                     body: 'Once you decide, we help with appointment scheduling, travel logistics, follow-up reminders and aftercare coordination.' },
];

const WHAT_WE_LOOK_AT = [
  'Treatment type and complexity',
  'Doctor experience with similar cases',
  'Clinic or hospital standards (facility, equipment, safety)',
  'Licensing and suitability for international patients',
  'Communication quality and English-language support',
  'International patient experience and aftercare protocols',
  'Location relative to where you are staying',
  'Indicative budget range you are comfortable with',
  'How urgent the treatment is',
  'Follow-up and revision policies',
  'Patient reviews and before-and-after evidence where available',
];

const TREATMENT_BLOCKS = [
  { to: '/cosmetic-surgery-in-vietnam',   label: 'Cosmetic surgeon recommendations',                sub: 'Korean & US-trained plastic surgeons at JCI-accredited centres' },
  { to: '/dental-treatments-in-vietnam',  label: 'Dentist & dental specialist recommendations',     sub: 'Implants, veneers, crowns and full-mouth restoration' },
  { to: '/hair-restoration-in-vietnam',   label: 'Hair transplant doctor recommendations',          sub: 'Sapphire FUE & DHI specialists with natural hairline design' },
  { to: '/eye-care-treatments-in-vietnam', label: 'Eye doctor & vision recommendations',            sub: 'LASIK, cataract, glaucoma and retina specialists' },
  { to: '/preventive-care-treatments-in-vietnam', label: 'Health checkup & specialist recommendations', sub: 'Executive health checks, cardiology, gastroenterology and more' },
  { to: '/urgent-medical-care',           label: 'Urgent medical doctor support',                   sub: 'For travelers & expats — hotel doctor, IV therapy, English-speaking telehealth' },
];

const CHOOSE_CHECKLIST = [
  'Check the doctor\'s qualifications and license',
  'Ask about experience with similar cases',
  'Ask where the treatment will be performed',
  'Confirm it is a licensed clinic or hospital',
  'Ask about risks and possible complications',
  'Ask what is included in the price (and what is not)',
  'Ask about follow-up care after treatment',
  'Ask who is responsible if anything goes wrong',
  'Ask for before-and-after photos where suitable',
  'Read patient reviews where available',
  'Make sure communication is clear in your language',
];

const QUESTIONS_TO_ASK = [
  'Are you licensed for this treatment?',
  'How many similar cases have you handled?',
  'Where will the consultation or procedure take place?',
  'Will the treatment be done in a licensed clinic or hospital?',
  'What are the risks and possible complications?',
  'What results are realistic for my case?',
  'What is included in the quote?',
  'How long should I stay in Vietnam?',
  'What aftercare is included?',
  'Who should I contact if I have concerns after treatment?',
];

const SUPPORT_JOURNEY = [
  { title: 'Patient sends enquiry',             body: 'A short message about treatment, city and timing. There is no charge to enquire.' },
  { title: 'VietClinics reviews basic needs',   body: 'Our coordinator reviews your case and identifies suitable doctor and clinic options.' },
  { title: 'Suitable options are suggested',     body: 'We share credentialed doctor/clinic options with notes on experience, language and indicative pricing.' },
  { title: 'Consultation is arranged',           body: 'We coordinate an online or in-person consultation so you can meet the doctor and ask questions.' },
  { title: 'Questions are prepared',             body: 'We share a tailored question set so you can compare doctors clearly during the consultation.' },
  { title: 'Translator support is arranged',     body: 'Where needed, we coordinate translator assistance so communication stays clear.' },
  { title: 'Plan & price are explained',         body: 'We help you understand the treatment plan, what is included, recovery time, and follow-up.' },
  { title: 'Next steps are coordinated',         body: 'If you decide to proceed, we coordinate appointments, travel logistics and aftercare.' },
];

const CITIES = [
  { name: 'Ho Chi Minh City', region: 'Southern Vietnam' },
  { name: 'Hanoi',            region: 'Northern Vietnam' },
  { name: 'Da Nang',          region: 'Central Coast' },
  { name: 'Hoi An',           region: 'Central Coast' },
  { name: 'Vung Tau',         region: 'Southern Coast' },
  { name: 'Nha Trang',        region: 'South Central Coast' },
  { name: 'Phu Quoc',         region: 'Island' },
];

const FAQ = [
  { q: 'Can VietClinics recommend doctors in Vietnam?',                            a: 'Yes. VietClinics shares doctor and clinic options from our verified partner network, with credentials, experience notes and language information, so you can make an informed choice. We never claim "the best doctor" — we help you find a suitable one for your case.' },
  { q: 'Is VietClinics a clinic or hospital?',                                      a: 'No. VietClinics is an independent healthcare coordination platform. We do not provide medical advice, diagnosis or treatment ourselves. All medical advice, diagnosis and treatment decisions come from licensed healthcare professionals at our partner clinics and hospitals.' },
  { q: 'Can VietClinics help me find an English-speaking doctor?',                 a: 'Yes. English-speaking communication is one of the things we check before suggesting any doctor or clinic. Where the lead doctor does not work fully in English, we coordinate translator support so consultations stay clear.' },
  { q: 'Can VietClinics help me choose between clinics?',                          a: 'Yes. We help you compare clinic and hospital options across qualifications, facilities, communication quality, indicative pricing and aftercare — so you can decide with confidence rather than guesswork.' },
  { q: 'Can VietClinics check if a doctor is suitable for my treatment?',          a: 'Yes. Before any recommendation, we review your case and confirm the doctor\'s experience with similar cases, scope of practice and credentials. The final clinical assessment is always done by the doctor themselves during consultation.' },
  { q: 'Can VietClinics help me book a doctor appointment?',                       a: 'Yes. Once you choose a doctor, we coordinate the appointment, share confirmation details and (where useful) help with travel logistics around the visit.' },
  { q: 'Can VietClinics help with online consultations?',                          a: 'Yes. Where appropriate, we arrange video consultations so you can speak with the doctor before any travel or commitment. Online consultations are often the fastest path to a clinician for non-urgent questions.' },
  { q: 'Can VietClinics help me find a cosmetic surgeon in Vietnam?',              a: 'Yes — see our Cosmetic Surgery in Vietnam hub for context. We coordinate with verified plastic surgeons at JCI-accredited surgical centres, and share full credentials before any consultation.' },
  { q: 'Can VietClinics help me find a dentist in Vietnam?',                       a: 'Yes — see our Dental Treatment in Vietnam hub. We coordinate with verified dental clinics for implants, veneers, crowns and full-mouth restoration, with transparent pricing and English-speaking support.' },
  { q: 'Does VietClinics provide medical advice?',                                  a: 'No. VietClinics does not provide medical advice, diagnosis or treatment recommendations. We are a coordination platform — all medical decisions must come from licensed healthcare professionals at our partner clinics and hospitals.' },
];

const RELATED_LINKS = [
  { to: '/patient-support-vietnam',        label: 'Patient Support in Vietnam',        sub: 'Translator help, treatment coordination and aftercare' },
  { to: '/medical-tourism-support-vietnam', label: 'Medical Tourism Support in Vietnam', sub: 'Treatment, travel & patient coordination for international patients' },
  { to: '/medical-translator-vietnam',    label: 'Medical Translator & Communication Support', sub: 'Appointment translation, question prep & treatment plan explanation' },
  { to: '/how-to-choose-doctor-vietnam',  label: 'How to Choose a Doctor in Vietnam', sub: 'Comparison criteria, red flags, questions to ask' },
  { to: '/how-it-works',                   label: 'How VietClinics Works',             sub: 'The 4-step concierge process explained' },
  { to: '/contact',                        label: 'Contact VietClinics',               sub: 'Talk to a coordinator on WhatsApp or email' },
  { to: '/cosmetic-surgery-in-vietnam',    label: 'Cosmetic Surgery in Vietnam',       sub: 'Verified plastic surgeons at JCI-accredited hospitals' },
  { to: '/dental-treatments-in-vietnam',   label: 'Dental Treatment in Vietnam',       sub: 'Implants, veneers and full-mouth restoration' },
  { to: '/hair-restoration-in-vietnam',    label: 'Hair Transplant in Vietnam',        sub: 'Sapphire FUE & DHI by Korean & Turkish-trained surgeons' },
  { to: '/eye-care-treatments-in-vietnam', label: 'Eye Treatment in Vietnam',          sub: 'LASIK, cataract and retina specialists' },
  { to: '/urgent-medical-care',            label: 'Urgent Medical Care in Vietnam',    sub: 'Hotel doctor, IV therapy & English-speaking telehealth' },
  { to: '/preventive-care-treatments-in-vietnam', label: 'Health Checkups in Vietnam', sub: 'Executive health and specialist screenings' },
];

const EDITORIAL = {
  reviewedBy: 'VietClinics Patient Concierge Team',
  role: 'Doctor & clinic coordinators',
  methodology:
    'Doctor and clinic recommendations are sourced from our verified partner network, vetted against an internal checklist covering qualifications, scope of practice, facility standards, language support and aftercare. Recommendations are case-specific, never "best-in-Vietnam" claims. Reviewed quarterly.',
  lastUpdated: '2026-02-15',
};

const DoctorRecommendationVietnamPage = () => {
  const { contactInfo } = useContactInfo();
  const { overrides } = useCatalog();
  const heroImage = getInfoLandingImage(overrides, 'doctor-recommendation', HERO_IMAGE);
  const whatsappHref = getWhatsAppHref(
    contactInfo,
    'Hi VietClinics, I would like a doctor recommendation in Vietnam.'
  );
  const canonical = `${SITE}${SLUG}`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Service',
        '@id': `${canonical}#service`,
        serviceType: 'Doctor Recommendation & Healthcare Coordination',
        provider: { '@type': 'Organization', name: 'VietClinics' },
        areaServed: [{ '@type': 'Country', name: 'Vietnam' }],
        url: canonical,
        description:
          'Independent doctor and clinic recommendations for international patients, expats and travelers in Vietnam. Verified partner network, English-speaking coordination, transparent indicative pricing.',
      },
      {
        '@type': 'WebPage',
        '@id': `${canonical}#webpage`,
        url: canonical,
        name: 'Doctor Recommendation in Vietnam',
        description:
          'VietClinics helps international patients, expats, travelers, and medical tourists in Vietnam find suitable doctors, compare clinics and hospitals, arrange consultations, and understand what to ask before choosing a healthcare provider.',
        inLanguage: 'en',
        dateModified: EDITORIAL.lastUpdated,
        lastReviewed: EDITORIAL.lastUpdated,
        reviewedBy: { '@type': 'Organization', name: EDITORIAL.reviewedBy },
      },
      {
        '@type': 'FAQPage',
        '@id': `${canonical}#faq`,
        mainEntity: FAQ.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${canonical}#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
          { '@type': 'ListItem', position: 2, name: 'Patient Support', item: `${SITE}/patient-support-vietnam` },
          { '@type': 'ListItem', position: 3, name: 'Doctor Recommendation', item: canonical },
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Doctor Recommendation in Vietnam | Find Suitable Doctors &amp; Clinics | VietClinics</title>
        <meta
          name="description"
          content="VietClinics helps international patients, expats, travelers, and medical tourists in Vietnam find suitable doctors, compare clinics and hospitals, arrange consultations, and understand what to ask before choosing a healthcare provider."
        />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content="Doctor Recommendation in Vietnam | VietClinics" />
        <meta
          property="og:description"
          content="Independent support to help international patients, expats and travelers find suitable doctors, clinics and specialists in Vietnam."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={heroImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Header />

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-white" data-testid="doctor-rec-hero">
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(135deg, #F0FAF8 0%, #DCEFEC 60%, #ffffff 100%)',
          }}
        />
        <div className="relative container mx-auto px-4 max-w-7xl py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[11px] font-bold text-[#1A8F8A] uppercase tracking-widest mb-4 inline-block bg-white/80 backdrop-blur px-3 py-1.5 rounded-full border border-[#DCEFEC]">
                Doctor recommendation · Vietnam
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-[1.05] mb-5">
                Doctor Recommendation in Vietnam
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed max-w-xl mb-7">
                Independent support to help international patients, expats, travelers
                and medical tourists find suitable doctors, clinics, hospitals and
                specialists in Vietnam.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                {whatsappHref && (
                  <a
                    href={whatsappHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-[#1FA7A1] hover:bg-[#138F8A] text-white font-bold rounded-full px-6 py-3.5 transition-colors"
                    data-testid="doctor-rec-cta-recommend"
                  >
                    <MessageCircle className="h-4 w-4" />
                    Ask for Doctor Recommendations
                  </a>
                )}
                <Link
                  to="/patient-support-vietnam"
                  className="inline-flex items-center justify-center gap-2 bg-white text-slate-900 border border-slate-200 hover:border-[#1FA7A1] font-bold rounded-full px-6 py-3.5 transition-colors"
                  data-testid="doctor-rec-cta-support"
                >
                  Request Patient Support
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
            <div
              className="relative rounded-3xl overflow-hidden bg-slate-100"
              style={{ aspectRatio: '4 / 3' }}
            >
              <img
                src={heroImage}
                onError={(e) => { e.currentTarget.src = HERO_FALLBACK; }}
                alt="VietClinics helping patient find suitable doctor in Vietnam"
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── TRUST STRIP ──────────────────────────────────────────────────── */}
      <section className="bg-white border-y border-slate-100" data-testid="doctor-rec-trust">
        <div className="container mx-auto px-4 max-w-7xl py-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: ShieldCheck,     label: 'Verified partner network' },
              { icon: Stethoscope,     label: 'Licensed doctors & clinics' },
              { icon: Languages,       label: 'English-speaking & translator support' },
              { icon: Heart,           label: 'Independent — not a hospital or clinic' },
            ].map(({ icon: Icon, label }, i) => (
              <div key={label} className="flex items-center gap-3" data-testid={`doctor-rec-trust-${i + 1}`}>
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: '#DCEFEC' }}
                >
                  <Icon className="h-4 w-4 text-[#1A8F8A]" />
                </div>
                <p className="text-xs font-bold text-slate-800 leading-tight">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── INTRODUCTION ─────────────────────────────────────────────────── */}
      <UrgentIntro
        heading="Finding a doctor in another country shouldn't feel like a guess."
        paragraphs={[
          'When you need medical treatment in a country you do not know well, it can be hard to know which doctor to trust, whether the clinic is suitable for international patients, whether the doctor really speaks English, or what to ask before treatment. Search results give you names, but not context.',
          'VietClinics helps make this process clearer. We share suitable doctor and clinic recommendations from our verified partner network, coordinate consultations, arrange translator support where needed, and walk through the questions you should ask before making any decision.',
          'We are an independent healthcare coordination platform — we are not a clinic or hospital, and we do not provide medical diagnosis or guarantee treatment results. All medical decisions come from licensed healthcare professionals at the clinics and hospitals we coordinate with.',
        ]}
        testId="doctor-rec-intro"
      />

      {/* ── HOW WE HELP YOU FIND A DOCTOR ────────────────────────────────── */}
      <UrgentCardGrid
        eyebrow="How VietClinics helps you find a doctor"
        heading="Eight ways we make doctor selection easier"
        items={HOW_WE_HELP}
        testId="doctor-rec-how"
      />

      {/* ── WHAT WE LOOK AT BEFORE RECOMMENDING ──────────────────────────── */}
      <section className="py-16 lg:py-20 bg-white" data-testid="doctor-rec-criteria">
        <div className="container mx-auto px-4 max-w-5xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
            What we look at before recommending a doctor
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-8">
            Our doctor recommendation framework
          </h2>
          <ul className="grid sm:grid-cols-2 gap-3">
            {WHAT_WE_LOOK_AT.map((item, i) => (
              <li
                key={i}
                className="flex items-start gap-3 bg-white border border-slate-100 rounded-2xl p-4"
                style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.04)' }}
                data-testid={`doctor-rec-criteria-${i + 1}`}
              >
                <ClipboardCheck className="h-5 w-5 text-[#1FA7A1] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-700 font-semibold leading-snug">{item}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── RECOMMENDATIONS BY TREATMENT TYPE ────────────────────────────── */}
      <section className="py-16 lg:py-20 bg-[#F7FAF9]" data-testid="doctor-rec-by-treatment">
        <div className="container mx-auto px-4 max-w-6xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
            Doctor recommendations by treatment
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-8">
            Find the right specialist for your treatment
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {TREATMENT_BLOCKS.map((b) => (
              <Link
                key={b.to}
                to={b.to}
                className="group bg-white hover:bg-[#F0FAF8] border border-slate-100 hover:border-[#DCEFEC] rounded-2xl p-5 transition-colors flex items-start justify-between gap-4"
                style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.04)' }}
                data-testid={`doctor-rec-treatment-${b.to.replace(/\W+/g, '-').replace(/^-|-$/g, '')}`}
              >
                <div>
                  <p className="font-extrabold text-slate-900 text-sm leading-tight">{b.label}</p>
                  <p className="text-xs text-slate-500 mt-1 leading-snug">{b.sub}</p>
                </div>
                <ArrowRight className="h-4 w-4 text-[#1FA7A1] flex-shrink-0 mt-1 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── ENGLISH-SPEAKING DOCTORS ─────────────────────────────────────── */}
      <section className="py-16 lg:py-20 bg-white" data-testid="doctor-rec-english">
        <div className="container mx-auto px-4 max-w-4xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
            English-speaking doctors in Vietnam
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-6">
            Clear communication is part of safe care
          </h2>
          <div className="space-y-5 text-base lg:text-lg text-slate-600 leading-relaxed">
            <p>
              Many international patients, expats and travelers prefer English-speaking
              doctors, or clinics with English-speaking staff. Communication matters —
              explaining symptoms, history, expectations and concerns clearly is part of
              safe medical care.
            </p>
            <p>
              VietClinics helps identify suitable English-speaking doctors and clinics
              from our verified partner network, and provides translator support where
              required. This makes our service especially useful for foreigners and
              medical tourists who want to be confident they are being understood — and
              that they understand their treatment plan in their own language.
            </p>
          </div>
        </div>
      </section>

      {/* ── HOW TO CHOOSE — CHECKLIST ────────────────────────────────────── */}
      <section className="py-16 lg:py-20 bg-[#F7FAF9]" data-testid="doctor-rec-checklist">
        <div className="container mx-auto px-4 max-w-5xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
            How to choose the right doctor in Vietnam
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-8">
            Practical checklist before you decide
          </h2>
          <ul className="grid sm:grid-cols-2 gap-3">
            {CHOOSE_CHECKLIST.map((item, i) => (
              <li
                key={i}
                className="flex items-start gap-3 bg-white border border-slate-100 rounded-2xl p-4"
                style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.04)' }}
                data-testid={`doctor-rec-checklist-${i + 1}`}
              >
                <CheckCircle2 className="h-5 w-5 text-[#1FA7A1] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-700 font-semibold leading-snug">{item}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── QUESTIONS TO ASK ─────────────────────────────────────────────── */}
      <section className="py-16 lg:py-20 bg-white" data-testid="doctor-rec-questions">
        <div className="container mx-auto px-4 max-w-4xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
            Questions to ask before booking a doctor
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-8">
            Prepare these for your consultation
          </h2>
          <div className="space-y-3">
            {QUESTIONS_TO_ASK.map((q, i) => (
              <div
                key={i}
                className="flex items-start gap-3 bg-white border border-slate-100 rounded-xl px-5 py-4"
                style={{ boxShadow: '0 2px 12px rgba(15,42,85,0.04)' }}
                data-testid={`doctor-rec-question-${i + 1}`}
              >
                <span
                  className="w-7 h-7 rounded-full flex items-center justify-center text-white font-extrabold text-xs flex-shrink-0 mt-0.5"
                  style={{ background: '#1FA7A1' }}
                >
                  {i + 1}
                </span>
                <p className="text-sm md:text-base text-slate-700 font-semibold leading-snug">{q}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY DOCTOR CHOICE MATTERS ────────────────────────────────────── */}
      <section className="py-16 lg:py-20 bg-[#F7FAF9]" data-testid="doctor-rec-why-matters">
        <div className="container mx-auto px-4 max-w-4xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
            Why doctor choice matters
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-6">
            Price alone is not the right way to choose a doctor
          </h2>
          <div className="space-y-5 text-base lg:text-lg text-slate-600 leading-relaxed">
            <p>
              Choosing a doctor based only on price can lead to disappointing outcomes.
              The right decision should also consider safety, doctor experience,
              communication, hospital or clinic standards, the quality of the treatment
              plan, and the aftercare you receive once treatment is done.
            </p>
            <p>
              We do not attack any providers — there are many capable doctors and
              clinics in Vietnam. What we do is help you compare based on what
              actually matters for your case, so you can choose with confidence and
              feel supported throughout the journey.
            </p>
          </div>
        </div>
      </section>

      {/* ── SUPPORT JOURNEY (steps) ──────────────────────────────────────── */}
      <UrgentSteps
        eyebrow="Support before, during and after the appointment"
        heading="The VietClinics doctor recommendation journey"
        steps={SUPPORT_JOURNEY}
        testId="doctor-rec-journey"
      />

      {/* ── CITIES COVERED ───────────────────────────────────────────────── */}
      <section className="py-16 lg:py-20 bg-white" data-testid="doctor-rec-cities">
        <div className="container mx-auto px-4 max-w-5xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
            Cities covered
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-8">
            Doctor recommendations across Vietnam
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {CITIES.map((c) => (
              <div
                key={c.name}
                className="bg-[#F0FAF8] border border-[#DCEFEC] rounded-2xl px-4 py-3"
                data-testid={`doctor-rec-city-${c.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <p className="font-extrabold text-slate-900 text-sm">{c.name}</p>
                <p className="text-xs text-slate-500 mt-0.5">{c.region}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-5 max-w-2xl">
            Availability of in-person services varies by city, specialty and timing.
            Online consultations are usually the fastest path to a clinician.
          </p>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <UrgentFaq
        heading="Doctor recommendations in Vietnam — frequently asked"
        items={FAQ}
        testId="doctor-rec-faq"
      />

      {/* ── RELATED INTERNAL LINKS ───────────────────────────────────────── */}
      <section className="py-16 lg:py-20 bg-white" data-testid="doctor-rec-related">
        <div className="container mx-auto px-4 max-w-6xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
            Continue exploring VietClinics
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-8">
            More patient support &amp; treatment guides
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {RELATED_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="group bg-[#F0FAF8] hover:bg-[#DCEFEC] border border-[#DCEFEC] rounded-2xl p-5 transition-colors flex items-start justify-between gap-4"
                data-testid={`doctor-rec-related-${link.to.replace(/\W+/g, '-').replace(/^-|-$/g, '')}`}
              >
                <div>
                  <p className="font-extrabold text-slate-900 text-sm leading-tight">{link.label}</p>
                  <p className="text-xs text-slate-500 mt-1 leading-snug">{link.sub}</p>
                </div>
                <ArrowRight className="h-4 w-4 text-[#1FA7A1] flex-shrink-0 mt-1 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── EDITORIAL / E-E-A-T ─────────────────────────────────────────── */}
      <UrgentEditorial editorial={EDITORIAL} testId="doctor-rec-editorial" />

      {/* ── IMPORTANT DISCLAIMER ─────────────────────────────────────────── */}
      <section className="py-10 bg-white" data-testid="doctor-rec-disclaimer-section">
        <div className="container mx-auto px-4 max-w-4xl">
          <div
            className="bg-amber-50/60 border border-amber-100 rounded-2xl p-5 lg:p-6 flex gap-3"
            data-testid="doctor-rec-disclaimer"
          >
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm leading-relaxed text-amber-900">
              <p className="font-bold mb-1">Important notice</p>
              <p>
                VietClinics is an independent healthcare coordination platform.
                VietClinics is <strong>not a hospital or clinic</strong> and does
                <strong> not provide medical diagnosis</strong>. All medical advice,
                diagnosis and treatment decisions must come from licensed healthcare
                professionals. For life-threatening emergencies in Vietnam, dial
                <strong> 115</strong>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ────────────────────────────────────────────────────── */}
      <UrgentCtaBand
        heading="Need Help Finding a Suitable Doctor in Vietnam?"
        subheading="Tell VietClinics what treatment or medical support you need, and our team will help you understand your options, arrange suitable doctor recommendations, and coordinate your next steps."
        testId="doctor-rec-final-cta"
      />

      <Footer />
    </div>
  );
};

export default DoctorRecommendationVietnamPage;
